import React, { useEffect, useState } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { RequiredLabel } from '../../utils/validators';
import { parseMoney, formatMoney } from '../../utils/money';
import JustifyInventoryOutput from './JustifyInventoryOutput';
import { getAll } from '../../services/Users/Users';
import { getItem, getItemFolioRanges } from '../../services/Items/Inventory';
import { getCategories } from '../../services/Configs/ItemCategoriesService';
import { getLocations } from '../../services/Configs/LocationsService';
import { getStatuses } from '../../services/Other/GeneralQueries';
import { serviceUpdateItem } from '../../services/Items/AddItem';
import { getSupplies } from '../../services/Supplies/Supplies';

const getUserId = () => sessionStorage.getItem('userId') ?? 0;

const formatFolio = (r) => {
    const unitLabel = r.units === 'CAJA_12' ? 'Caja (12)' : 'Botella 750';
    const count = Number(r.count ?? 0);
    const isSingle = count <= 1 || r.startFolioName === r.endFolioName;
    return `${unitLabel}: ${isSingle ? r.startFolioName : `${r.startFolioName} – ${r.endFolioName} (${r.count})`}`;
};

function EditItemModal({ show, id, onClose, onSuccess }) {
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [users, setUsers] = useState([]);

    const [oldQty, setOldQty] = useState(0);
    const [differenceInQuantity, setDifferenceInQuantity] = useState(0);
    const [showJustifyModal, setShowJustifyModal] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [justifyBy, setJustifyBy] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const [folioRanges, setFolioRanges] = useState([]);

    const [finalPriceText, setFinalPriceText] = useState(formatMoney(0));
    const [finalCostText, setFinalCostText] = useState(formatMoney(0));

    const [supplies, setSupplies] = useState([]);
    const [selectedSupplyIds, setSelectedSupplyIds] = useState([]);

    const [formData, setFormData] = useState({
        id: 0,
        name: '',
        description: '',
        categoryId: 0,
        quantity: '1',
        unitPrice: '0',
        locationId: 0,
        statusId: 0,
        unitOfWeightMeasurement: '',
        acquisitionDate: '',
        userId: getUserId(),
        assignedToUserId: 0,
        differenceInQuantity: 0,
        selectedStatuses: [],
        selectedLocations: [],
        selectedUsers: [],
    });

    useEffect(() => {
        if (!show || !id) return;

        (async () => {
            const [cats, locs, stats, us, sups] = await Promise.all([
                getCategories(),
                getLocations(),
                getStatuses(),
                getAll(),
                getSupplies(),
            ]);

            setCategories(Array.isArray(cats) ? cats : []);
            setLocations(Array.isArray(locs) ? locs : []);
            setStatuses(Array.isArray(stats) ? stats : []);
            setUsers(Array.isArray(us) ? us : []);
            setSupplies(Array.isArray(sups) ? sups : []);

            const item = await getItem(id);
            const fp = item.final_price ?? item.finalPrice ?? 0;

            setFinalPriceText(formatMoney(fp));
            setFinalCostText(formatMoney(item.unitPrice ?? 0));

            setFormData((p) => ({
                ...p,
                id: item.id,
                name: item.name,
                description: item.description,
                categoryId: item.categoryId,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                locationId: item.locationId,
                statusId: item.statusId,
                unitOfWeightMeasurement: item.unitOfWeightMeasurement,
                acquisitionDate: item.acquisitionDateInputFormatted,
                userId: getUserId(),
                assignedToUserId: item.assignedToUserId,
                differenceInQuantity: 0,
                selectedStatuses: [],
                selectedLocations: [],
                selectedUsers: [],
                final_price: fp,
            }));

            setOldQty(item.quantity);
            const ranges = await getItemFolioRanges(id);
            setFolioRanges(Array.isArray(ranges) ? ranges : []);

            const ids =
                Array.isArray(item.supplyIds) && item.supplyIds.length
                    ? item.supplyIds
                    : (Array.isArray(item.supplies) ? item.supplies.map(s => s.id) : []);

            setSelectedSupplyIds(ids.map(Number).filter(n => n > 0));
        })();
    }, [show, id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const pending = [];

        if (!formData.name) pending.push('Nombre');
        if (formData.categoryId == 0) pending.push('Categoría');
        if (!formData.quantity) pending.push('Cantidad de objetos');
        if (formData.locationId == 0) pending.push('Ubicación');
        if (formData.statusId == 0) pending.push('Estado');

        if (oldQty > formData.quantity) {
            setDifferenceInQuantity(oldQty - formData.quantity);
            pending.push('Es necesario justificar la salida');
            setShowJustifyModal(true);
        }

        if (pending.length) {
            toast.warning(`Debes completar: ${pending.join(', ')}`);
            return;
        }

        const payload = {
            ...formData,
            unitPrice: parseMoney(finalCostText),
            final_price: parseMoney(finalPriceText),
            supplyIds: selectedSupplyIds.map(Number),
        };

        await serviceUpdateItem(payload);
        toast.success('Se actualizó correctamente');
        onSuccess?.();
    };

    const handleSaveFromModal = async () => {
        // misma lógica que en tu EditItem.js
        if (justifyBy === 'byGroup') {
            if (selectedStatuses.length === 0 || selectedLocations.length === 0) {
                if (selectedStatuses.length === 0) toast.warning('Debes seleccionar el nuevo estatus');
                if (selectedLocations.length === 0) toast.warning('Debes seleccionar la nueva ubicación');
                return;
            }
        } else if (justifyBy === 'byIndividual') {
            if (selectedStatuses.length !== differenceInQuantity || selectedLocations.length !== differenceInQuantity) {
                if (selectedStatuses.length !== differenceInQuantity) toast.warning('Debes seleccionar el nuevo estatus para cada objeto');
                if (selectedLocations.length !== differenceInQuantity) toast.warning('Debes seleccionar la nueva ubicación para cada objeto');
                return;
            }
        }

        setIsSaveDisabled(true);
        const updated = {
            ...formData,
            unitPrice: parseMoney(finalCostText),
            final_price: parseMoney(finalPriceText),
            supplyIds: selectedSupplyIds.map(Number),
            differenceInQuantity,
            selectedStatuses,
            selectedLocations,
            selectedUsers,
        };

        await serviceUpdateItem(updated);
        toast.success('Se actualizó correctamente');
        setShowJustifyModal(false);
        onSuccess?.();
    };

    return (
        <>
            <Modal size="lg" show={show} onHide={onClose} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Editar objeto</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Row className="justify-content-center">
                        <Col xl={10}>
                            <Form onSubmit={handleSubmit}>
                                {/* NOMBRE */}
                                <Row className='mt-3' >
                                    <Col>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Nombre:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* DESCRIPCIÓN */}
                                <Row className='mt-3' >
                                    <Col>
                                        <Form.Group controlId="">
                                            <Form.Label style={{ fontWeight: 'bold' }}>Descripción :</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* FECHA DE ADQUISICIÓN | UNIDAD DE MEDIDA */}
                                <Row className="mt-3 g-3">
                                    <Col md={6}>
                                        <Form.Label as={RequiredLabel} required>Fecha de adquisición:</Form.Label>
                                        <Form.Group controlId="formEmail">
                                            <Form.Control
                                                type="date"
                                                name="acquisitionDate"
                                                placeholder=''
                                                value={formData.acquisitionDate}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={6}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Unidad de medida:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.unitOfWeightMeasurement || ''}
                                                disabled
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* CANTIDAD DE OBJETOS | COSTO UNITARIO */}
                                <Row className="mt-3 g-3">
                                    <Col md={6}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Cantidad de objetos:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="quantity"
                                                value={formData.quantity}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={6}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Costo unitario $:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                inputMode="decimal"
                                                value={finalCostText}
                                                onChange={(e) => setFinalCostText(e.target.value)}
                                                onBlur={() => {
                                                    const n = parseMoney(finalCostText);
                                                    setFinalCostText(formatMoney(n));
                                                    setFormData((p) => ({ ...p, unitPrice: n }));
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* PRECIO FINAL | CATEGORÍA */}
                                <Row className="mt-3 g-3" >
                                    <Col xl={6}>
                                        <Form.Group>
                                            <Form.Label as={RequiredLabel} required>Precio final $:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                inputMode="decimal"
                                                value={finalPriceText}
                                                onChange={(e) => setFinalPriceText(e.target.value)}
                                                onBlur={() => {
                                                    const n = parseMoney(finalPriceText);
                                                    setFinalPriceText(formatMoney(n));
                                                    setFormData((p) => ({ ...p, final_price: n }));
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={6}>
                                        <Form.Group controlId="formRole">
                                            <Form.Label as={RequiredLabel} required>Categoría:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={categories.find(c => Number(c.id) === Number(formData.categoryId))?.name || ''}
                                                disabled
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* UBICACIÓN | ESTATUS */}
                                <Row className="mt-3 g-3" >
                                    <Col xl={6}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Ubicación:</Form.Label>
                                            <Form.Control
                                                as="select"
                                                name="locationId"
                                                value={formData.locationId}
                                                onChange={handleInputChange}
                                            >
                                                <option key={0} value={0}>Seleccionar ubicación</option>
                                                {locations.map((location) => (
                                                    <option key={location.id} value={location.id}>{location.name}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col xl={6}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Estado:</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={statuses.find(s => Number(s.id) === Number(formData.statusId))?.name || ''}
                                                disabled
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* ASIGNADO A */}
                                <Row className="mt-3 g-3" >
                                    <Col xl={12}>
                                        <Form.Group controlId="">
                                            <Form.Label as={RequiredLabel} required>Asignado a:</Form.Label>
                                            <Form.Control
                                                as="select"
                                                name="assignedToUserId"
                                                value={formData.assignedToUserId}
                                                onChange={handleInputChange}
                                            >
                                                <option key={0} value={0}>Sin asignar</option>
                                                {users.map((user) => (
                                                    <option key={user.id} value={user.id}>{user.userName} - {user.email}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* CLAVE */}
                                <Row className="mt-3 g-3" >
                                    <Col xl={12}>
                                        <Form.Group className="mt-3">
                                            <Form.Label>Claves:</Form.Label>
                                            <Form.Select
                                                key={id}
                                                multiple
                                                value={selectedSupplyIds.map(String)}
                                                onChange={(e) => {
                                                    const vals = Array.from(e.target.selectedOptions).map(o => Number(o.value));
                                                    setSelectedSupplyIds(vals);
                                                }}
                                            >
                                                {supplies.map(s => (
                                                    <option key={s.id} value={String(s.id)}>
                                                        {s.code} - {s.product_name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <small className="text-muted">
                                                Puedes seleccionar varias claves para este producto.
                                            </small>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/* FOLIOS ASIGNADOS (con regla: si count=1 muestra solo un folio) */}
                                <Row className="mt-3">
                                    <Col>
                                        <Form.Label style={{ fontWeight: 'bold' }}>Folios asignados:</Form.Label>
                                        {folioRanges.length > 0 ? (
                                            <ul className="mb-0">
                                                {folioRanges.map((r, i) => (
                                                    <li key={i}>{formatFolio(r)}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-muted">Sin folios asignados</p>
                                        )}
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
                                    <Button variant="primary" type="submit">Actualizar objeto</Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal >

            <JustifyInventoryOutput
                show={showJustifyModal}
                handleClose={() => setShowJustifyModal(false)}
                differenceInQuantity={differenceInQuantity}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                selectedLocations={selectedLocations}
                setSelectedLocations={setSelectedLocations}
                selectedUsers={selectedUsers}
                setSelectedUsers={setSelectedUsers}
                onSave={handleSaveFromModal}
                setJustifyBy={setJustifyBy}
                justifyBy={justifyBy}
                isSaveDisabled={isSaveDisabled}
            />
        </>
    )
};

export default EditItemModal;